import { inject } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { AuthenticationService } from '../../shared/authentication.service';
import { startAuthorizationRequest } from '../../shared/authorization-request';
import { SessionRecoveryCoordinatorService } from '../../shared/session-recovery-coordinator.service';

/**
 * `AUTH_GUARD` of the local strategy. A missing session does not render a login form: it starts an
 * authorization code + PKCE request, and the backend either answers with a code (live hub session)
 * or sends the user to the sign-in form with a `continue` back to this request.
 *
 * `/sign-out` is never recorded as the post-login destination.
 */
export const authenticationLocalGuard: CanActivateFn = (route, state) => {
  const authenticationService = inject(AuthenticationService);
  const coordinator = inject(SessionRecoveryCoordinatorService);

  return authenticationService.check().pipe(
    switchMap((authenticated) => {
      if (!authenticated) {
        // A tab reloading under a sibling's identity also resolves `false`: starting a request
        // here could supersede that reload and overwrite the sibling's session.
        if (coordinator.isReloadingTab()) return of(false);
        void startAuthorizationRequest(state.url === '/sign-out' ? '' : state.url);
        return of(false);
      }

      return of(true);
    }),
  );
};
